import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getApiBaseUrl, getLoginAppUrl } from '@/config/runtimeUrls';

const REQUEST_TIMEOUT_MS = 30_000;

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = String(token || '').split('.')[1] || '';
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const sanitizeReturnToPath = (rawValue: string | null, fallbackPath: string): string => {
  const value = String(rawValue || '').trim();
  if (!value) return fallbackPath;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  if (typeof window !== 'undefined' && /^https?:\/\//i.test(value)) {
    try {
      const target = new URL(value);
      if (target.origin === window.location.origin) {
        return `${target.pathname}${target.search}${target.hash}`;
      }
    } catch (_) {
      // no-op
    }
  }
  return fallbackPath;
};

const extractErrorMessage = (payload: any, fallback: string): string =>
  String(
    payload?.error?.message ||
      payload?.message ||
      payload?.data?.message ||
      payload?.data?.error ||
      payload?.error ||
      fallback
  );

const fetchWithTimeout = async (url: string, init: RequestInit, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const persistSession = (data: any) => {
  const accessToken = String(data?.accessToken || data?.token || '').trim();
  if (!accessToken) {
    throw new Error('No access token returned from sign-in continuation.');
  }

  const refreshToken = String(data?.refreshToken || '').trim();
  const payload = decodeJwtPayload(accessToken) || {};
  const user = {
    id: String(payload.sub || data?.user?.id || ''),
    email: String(payload.email || data?.user?.email || ''),
    name: String(payload.name || data?.user?.name || data?.user?.email || ''),
    role: String(payload.role || data?.user?.role || ''),
    systemRole: String(payload.systemRole || data?.user?.systemRole || ''),
    tenantId: String(payload.tenantId || data?.tenantId || data?.user?.tenantId || ''),
    tenantSlug: String(payload.tenantSlug || data?.tenantSlug || data?.user?.tenantSlug || ''),
  };

  const exp = Number(payload.exp || 0);
  const expiresAt = exp > 0 ? exp * 1000 : Date.now() + 60 * 60 * 1000;

  localStorage.setItem('auth_token', accessToken);
  localStorage.setItem('auth_v2_accessToken', accessToken);
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('token', accessToken);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('auth_v2_refreshToken', refreshToken);
    localStorage.setItem('refreshToken', refreshToken);
  }
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem(
    'authData',
    JSON.stringify({
      token: accessToken,
      user,
      tenantId: user.tenantId || null,
      expiresAt,
    })
  );
  if (user.tenantId) localStorage.setItem('tenantId', user.tenantId);
  if (user.tenantSlug) localStorage.setItem('tenantSlug', user.tenantSlug);
};

export default function AuthContinuePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState(true);

  useEffect(() => {
    let active = true;

    const run = async () => {
      const params = new URLSearchParams(location.search);
      const token = String(params.get('token') || '').trim();
      const returnTo = sanitizeReturnToPath(params.get('returnTo'), '/dashboard');
      if (!token) {
        if (active) {
          setError('Your sign-in link is missing a token. Please sign in again.');
          setInFlight(false);
        }
        return;
      }

      try {
        const response = await fetchWithTimeout(
          `${getApiBaseUrl()}/auth/continue`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ token }),
          },
          REQUEST_TIMEOUT_MS
        );

        const payload = await response.json().catch(() => ({}));
        const data = payload?.data ?? payload;
        if (!response.ok || payload?.success === false) {
          throw new Error(extractErrorMessage(payload, 'Unable to complete sign-in.'));
        }

        persistSession(data);
        navigate(returnTo, { replace: true });
      } catch (err: any) {
        if (!active) return;
        if (err?.name === 'AbortError') {
          setError('Sign-in timed out after 30 seconds. Please try again.');
        } else {
          setError(err?.message || 'Unable to complete sign-in. Please try again.');
        }
      } finally {
        if (active) {
          setInFlight(false);
        }
      }
    };

    void run();

    return () => {
      active = false;
    };
  }, [location.search, navigate]);

  if (inFlight && !error) {
    return null;
  }

  if (!error) {
    return null;
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: '#0b1220',
        color: '#e5edf8',
        padding: 24,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 14,
          border: '1px solid rgba(148, 163, 184, 0.25)',
          background: 'rgba(17, 24, 39, 0.92)',
          padding: 24,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24, color: '#fff' }}>Unable to finish sign-in</h1>
        <p style={{ marginTop: 12, marginBottom: 18, color: '#9fb0ca', lineHeight: 1.5 }}>{error}</p>
        <button
          type="button"
          onClick={() => window.location.replace(`${getLoginAppUrl()}/login`)}
          style={{
            border: 'none',
            borderRadius: 9,
            background: '#f97316',
            color: '#fff',
            fontWeight: 700,
            padding: '10px 14px',
            cursor: 'pointer',
          }}
        >
          Return to login
        </button>
      </section>
    </main>
  );
}
